import React, { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminPagination, {
  AdminSearchRow,
} from '../../components/admin/AdminPagination';
import { ConfirmModal } from '../../components/dicionarios';
import useAdminList from '../../hooks/useAdminList';
import { revalidateCadastroOperacional } from '../../hooks/clearCache';
import * as adminProcedimentos from '../../services/adminProcedimentos';
import handleFieldChange from '../../components/admin/formHelpers';

const emptyForm = {
  codProcedi: '',
  descProcedi: '',
  tipo: '',
  origem: '',
  pesquisa: '',
  laudoWeb: '',
  faturaWeb: 'S',
};

export default function Procedimentos() {
  const list = useAdminList(
    'admin-procedimentos',
    adminProcedimentos.listProcedimentos
  );
  const [formData, setFormData] = useState(emptyForm);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectItem = index => {
    const item = list.items[index];
    if (!item) return;
    setSelectedIndex(index);
    setFormData({
      codProcedi: item.codProcedi ?? '',
      descProcedi: item.descProcedi ?? '',
      tipo: item.tipo ?? '',
      origem: item.origem ?? '',
      pesquisa: item.pesquisa ?? '',
      laudoWeb: item.laudoWeb ?? '',
      faturaWeb: item.faturaWeb ?? '',
    });
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleEdit = () => {
    if (!list.items.length) {
      toast.warn('Nenhum registro para editar!');
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (list.items.length) selectItem(Math.min(selectedIndex, list.items.length - 1));
    else setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (!formData.descProcedi?.trim()) {
      toast.warn('Descrição é obrigatória!');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        descProcedi: formData.descProcedi.trim(),
        tipo: formData.tipo || null,
        origem: formData.origem || null,
        pesquisa: formData.pesquisa || null,
        laudoWeb: formData.laudoWeb || null,
        faturaWeb: formData.faturaWeb || null,
      };
      if (isCreating) {
        if (formData.codProcedi) payload.codProcedi = Number(formData.codProcedi);
        await adminProcedimentos.createProcedimento(payload);
        toast.success('Procedimento criado!');
      } else {
        await adminProcedimentos.updateProcedimento(
          Number(formData.codProcedi),
          payload
        );
        toast.success('Procedimento atualizado!');
      }
      setIsCreating(false);
      setIsEditing(false);
      await list.mutate();
      revalidateCadastroOperacional();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar procedimento!');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminProcedimentos.deleteProcedimento(Number(formData.codProcedi));
      toast.success('Procedimento excluído!');
      setShowDeleteConfirm(false);
      await list.mutate();
      revalidateCadastroOperacional();
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao excluir procedimento!');
    }
  };

  return (
    <AdminPageShell
      title="Cadastro de Procedimentos"
      subtitle="Parâmetros operacionais (ARQ_PROCEDIMENTOS)"
    >
      <AdminSearchRow q={list.q} onChange={list.setQ} placeholder="Buscar procedimento" />

      {list.isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Cód.</th>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Origem</th>
                  <th>Pesq.</th>
                  <th>Fatura Web</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item, index) => (
                  <tr
                    key={item.codProcedi}
                    onClick={() => selectItem(index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        index === selectedIndex && !isCreating
                          ? '#e2f0f0'
                          : undefined,
                    }}
                  >
                    <td>{item.codProcedi}</td>
                    <td>{item.descProcedi}</td>
                    <td>{item.tipo}</td>
                    <td>{item.origem}</td>
                    <td>{item.pesquisa}</td>
                    <td>{item.faturaWeb}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {!list.items.length && (
            <Alert variant="light" className="text-center">
              Nenhum procedimento encontrado.
            </Alert>
          )}
          <AdminPagination
            page={list.page}
            total={list.total}
            limit={list.limit}
            onPageChange={list.setPage}
          />
        </>
      )}

      <hr />
      <Form>
        <Row>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Código</Form.Label>
              <Form.Control
                value={formData.codProcedi ?? ''}
                disabled={!isCreating}
                onChange={handleFieldChange(setFormData, 'codProcedi')}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                value={formData.descProcedi}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'descProcedi')}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.tipo}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'tipo')}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Origem</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.origem}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'origem')}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Pesquisa</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.pesquisa}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'pesquisa')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Laudo Web</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.laudoWeb}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'laudoWeb')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Fatura Web</Form.Label>
              <Form.Control
                maxLength={1}
                value={formData.faturaWeb}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'faturaWeb')}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <div className="d-flex flex-wrap mt-3">
        <Button variant="primary" className="mr-2 mb-2" onClick={handleNew} disabled={isEditing}>
          Novo
        </Button>
        <Button variant="secondary" className="mr-2 mb-2" onClick={handleEdit} disabled={isEditing || !list.items.length}>
          Editar
        </Button>
        <Button variant="success" className="mr-2 mb-2" onClick={handleSave} disabled={!isEditing || isSaving}>
          {isSaving ? 'Gravando...' : 'Gravar'}
        </Button>
        <Button variant="warning" className="mr-2 mb-2" onClick={handleCancel} disabled={!isEditing}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          className="mb-2"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isCreating || !formData.codProcedi || isEditing}
        >
          Excluir
        </Button>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Excluir procedimento"
        message="Confirma a exclusão deste procedimento?"
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
    </AdminPageShell>
  );
}
